<template>
  <div>
    <main-layout
      :showPoweredBy="true"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
    <div class="container">
      <grid-row
        class="margin-top-xl"
        column-align="center">
        <grid-col col-size="4">
          <div
            class="header-wrapper">
            <div
              class="title-wrapper">
              <chevron
                @click="back()"/>
              <h1
                class="header-title">
                {{ $t('explorer.parameterSelection') }}
              </h1>
              <h2>{{ $t('explorer.multipleCorrelation') }}</h2>
            </div>
            <div>
              <btn-pill
                v-has-feature="'Query'"
                v-on:click="goToQueries()"
                btn-size="s"
                btn-type="secondary">
                <font-awesome-icon
                  :icon="icon.queries"/>
                {{ $t('menu.queries') }}
              </btn-pill>
              <btn-pill
                v-has-feature="'AggregateVariable'"
                v-on:click="goToAggregateVariables()"
                btn-size="s"
                btn-type="secondary">
                <font-awesome-icon
                  :icon="icon.aggregateVariables "/>
                {{ $t('aggregateVariable.addAggregateVariable') }}
              </btn-pill>
            </div>
          </div>
        </grid-col>
      </grid-row>
      <grid-row
        class="margin-top-xl"
        column-align="center">
        <grid-col
          col-size="4">
          <div
            class="selection-title">
            {{ $t('explorer.selectionVariables') }}
          </div>
          <tags-filter
            :text="'X: ' + $t('variables.filter')"
            class="margin-top-l"
            select-width="32rem"
            v-model="filterTagsX"
            :tagsTree="getTagsTreeState.filter(node => node.data.id > 0)"/>
          <tags-filter
            :text="'Y: ' + $t('variables.filter')"
            class="margin-top-l"
            select-width="32rem"
            v-model="filterTagsY"
            :tagsTree="getTagsTreeState.filter(node => node.data.id > 0)"/>
          <tags-selector
            class="margin-top-l"
            :max-values="maxNumberVariablePairs()"
            v-model="pairsSelected"
            :options="pairsOptions">
          </tags-selector>
          <div
            class="selection-title margin-top-xxl">
            {{ $t('explorer.datesSelection') }}
          </div>
          <date-range-selector
            ref="dateRangeSelector"
            v-model="selectedDates"/>
          <div
            class="action-wrapper margin-top-xxl">
            <btn-pill
              v-on:click="onClickCancel()"
              :btnText="$t('commons.cancel')"
              btn-size="m"
              btn-type="secondary">
            </btn-pill>
            <btn-pill
              class="margin-left-s"
              :disabled="isExploreDisabled()"
              v-on:click="onClickExplore()"
              :btnText="$t('explorer.menu.explore')"
              btn-size="m"
              btn-type="primary">
            </btn-pill>
          </div>
        </grid-col>
      </grid-row>
    </div>
    </main-layout>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MainLayout from '../MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import BtnPill from '@/components/shared/buttons/BtnPill';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faCalculatorAlt,
  faFileChartLine
} from '@fortawesome/pro-light-svg-icons';
import Alert from '@/components/shared/alert/Alert';
import DateRangeSelector from '@/components/explorer/DateRangeSelector';
import Chevron from '@/components/shared/Chevron';
import TagsFilter from '@/components/variable/TagsFilter';
import TagsSelector from '@/components/explorer/multiple/TagsSelector';
import variableApi from '@/api/variable';

export default {
  name: 'ExplorerMultipleCorrelationVariables',
  components: {
    TagsSelector,
    TagsFilter,
    MainLayout,
    GridRow,
    GridCol,
    BtnPill,
    FontAwesomeIcon,
    Alert,
    DateRangeSelector,
    Chevron
  },
  watch: {
    filterTagsX: {
      handler: function (newOptions, oldOptions) {
        if (hasFilterChanges(newOptions, oldOptions)) {
          this.updatePairs();
        }
      }
    },
    filterTagsY: {
      handler: function (newOptions, oldOptions) {
        if (hasFilterChanges(newOptions, oldOptions)) {
          this.updatePairs();
        }
      }
    }
  },
  data () {
    return {
      pairsSelected: [],
      pairsOptions: [],
      filterTagsX: [],
      tagsX: [],
      filterTagsY: [],
      tagsY: [],
      selectedDates: [],
      icon: {
        queries: faFileChartLine,
        aggregateVariables: faCalculatorAlt
      }
    };
  },
  created () {
    this.retrieveTagsTree({ context: this });
  },
  mounted () {
    const query = this.$route.query;
    if (Object.keys(query).length > 0) {
      this.filterTagsX = query.filterTagsX.split(',').map(i => Number(i));
      this.filterTagsY = query.filterTagsY.split(',').map(i => Number(i));
      this.pairsSelected = query.pairsSelected.split(',').map(i => Number(i));

      const beginDate = query.beginDate;
      const endDate = query.endDate;
      if (beginDate && endDate) {
        this.selectedDates = [new Date(Number(beginDate)), new Date(Number(endDate))];
        this.$refs.dateRangeSelector.selectCustomRange();
      }
    }
  },
  computed: {
    ...mapGetters([
      'getTagsTreeState'
    ]),
    getBreadcrumb () {
      return [
        {name: this.$t('explorer.breadcrumbMultipleCorrelation'), link: '/explore'},
        {name: this.$t('explorer.parameterSelection'), disabled: true}
      ];
    }
  },
  methods: {
    ...mapActions([
      'retrieveTagsTree'
    ]),
    back () {
      this.$router.push({name: 'Explore', query: this.$route.query});
    },
    updatePairs () {
      this.calculatePairsOptions();
    },
    goToQueries () {
      this.$router.push({name: 'QueryList', query: {filter: 'explorer-multiple-correlation', origin: 'ExplorerMultipleCorrelationVariables'}});
    },
    goToAggregateVariables () {
      this.$router.push({name: 'AggregateOperatedVariable', query: {origin: 'ExplorerCorrelationVariables'}});
    },
    onClickExplore () {
      const query = {
        filterTagsX: this.filterTagsX.join(),
        filterTagsY: this.filterTagsY.join(),
        pairsSelected: this.pairsSelected.join(),
        beginDate: this.selectedDates[0].getTime(),
        endDate: this.selectedDates[1].getTime()
      };

      this.$router.push({path: 'explorer-multiple-correlation', query: query});
    },
    onClickCancel () {
      this.filterTagsX = [];
      this.filterTagsY = [];
      this.pairsSelected = [];
    },
    isExploreDisabled () {
      return this.pairsSelected.length === 0;
    },
    calculatePairsOptions () {
      if (this.filterTagsX.length > 0 && this.filterTagsY.length > 0 && (hasFilterChanges(this.tagsX, this.filterTagsX) || hasFilterChanges(this.tagsY, this.filterTagsY))) {
        variableApi.getMultipleCorrelationVariablePairs(this, this.filterTagsX.join(), this.filterTagsY.join()).then(data => {
          this.pairsOptions = data.body;
          const query = this.$route.query;
          if (query.pairsSelected) {
            this.$router.push({query: {filterTagsX: query.filterTagsX, filterTagsY: query.filterTagsY, beginDate: query.beginDate, endDate: query.endDate}});
          }
          this.pairsSelected.forEach(selectedPair => {
            if (!this.pairsOptions.map(option => option.id).includes(selectedPair)) {
              this.pairsSelected.splice(this.pairsSelected.indexOf(selectedPair), 1);
            }
          });
        });
      } else {
        this.pairsOptions = [];
        if (this.filterTagsX.length === 0 || this.filterTagsY.length === 0) {
          this.pairsSelected = [];
        }
      }
      this.tagsX = this.filterTagsX.slice();
      this.tagsY = this.filterTagsY.slice();
    },
    maxNumberVariablePairs () {
      return MAXIMUM_NUMBER_OF_ALLOWED_VARIABLE_PAIRS;
    }
  }
};

const MAXIMUM_NUMBER_OF_ALLOWED_VARIABLE_PAIRS = 4;

/**
 * Check to prevent URL (query) override.
 * @param a New filter.
 * @param b Older filter.
 * @returns {boolean}
 */
const hasFilterChanges = (a, b) => {
  if (a.length !== b.length) {
    return true;
  }

  for (let i = 0; i < a.length; ++i) {
    const element = b.find(e => e === a[i]);
    if (!element) {
      return true;
    }
  }
};

</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";
  .header-wrapper {
    display: flex;
    justify-content: space-between;
  }

  .title-wrapper {
    display: flex;
  }

  h1 {
    margin-right: $sp-margin;
    padding-right: $sp-margin;
    border-right: 1px solid $black-trans6;
  }

  h2 {
    line-height: 2.2;
    font-size: 1.2rem;
    font-weight: lighter;
  }

  .selection-title {
    font-size: 1.2rem;
    margin-bottom: $sp-margin-small;
  }

  .action-wrapper {
    float: right;
    margin-top: $sp-margin-small;
  }
</style>



// WEBPACK FOOTER //
// src/layouts/explorer/ExplorerMultipleCorrelationVariables.vue